import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import DialogBase from './DialogBase';
import ProgressBar from '../ProgressBar';
import ErrorMessage from '../ErrorMessage';

import { portfolioService } from '../../../services/portfolio.service';
import { authService } from '../../../services/auth.service';


interface DialogPortfolioConsortiumsProps {
  open: boolean;
  portfolioId?: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

interface PortfolioConsortiumItem {
  consortiumId: string;
  name: string;
  members: string[];
}

interface ConsortiumOption {
  id: string;
  name: string;
  members: string[];
}

const DialogPortfolioConsortiums: React.FC<DialogPortfolioConsortiumsProps> = ({
  open,
  portfolioId,
  onClose,
  onUpdated,
}) => {
  const isReadOnly = authService.isReadOnly();
  const [linkedConsortiums, setLinkedConsortiums] = useState<PortfolioConsortiumItem[]>([]);
  const [options, setOptions] = useState<ConsortiumOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ConsortiumOption[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!portfolioId) return;

    setLoading(true);
    setError(null);

    try {
      const [portfolio, consortiumResponse] = await Promise.all([
        portfolioService.getPortfolio(portfolioId),
        portfolioService.getConsortiums({ _offset: 0, _limit: 100 }),
      ]);

      const consortiumItems = consortiumResponse?.items ?? consortiumResponse ?? [];

      const allOptions = consortiumItems
        .map((item: any) => {
          const members = Array.isArray(item?.members)
            ? item.members
                .map((member: any) => {
                  const memberId = member?.id;
                  const memberName = member?.displayName || member?.name || member?.id;
                  return { id: memberId, name: memberName };
                })
                .filter((m: any) => m?.id && m?.name)
                .sort((a: any, b: any) => a.name.localeCompare(b.name, 'nl-NL'))
                .map((m: any) => m.name)
            : [];
          
          return {
            id: item?.id,
            name: item?.name,
            members: members,
          };
        })
        .filter((item: ConsortiumOption) => Boolean(item.id && item.name))
        .sort((a: ConsortiumOption, b: ConsortiumOption) => a.name.localeCompare(b.name, 'nl-NL'));

      const optionById = allOptions.reduce((acc: Record<string, ConsortiumOption>, option: ConsortiumOption) => {
        acc[option.id] = option;
        return acc;
      }, {});

      const linkedItems = Array.isArray(portfolio?.consortiums)
        ? portfolio.consortiums
            .map((item: any) => ({
              consortiumId: item?.consortiumId,
              name: item?.name,
              members: optionById[item?.consortiumId]?.members ?? [],
            }))
            .filter((item: PortfolioConsortiumItem) => Boolean(item.consortiumId && item.name))
            .sort((a: PortfolioConsortiumItem, b: PortfolioConsortiumItem) =>
              a.name.localeCompare(b.name, 'nl-NL')
            )
        : [];

      setLinkedConsortiums(linkedItems);
      setOptions(allOptions);
      setSelectedOptions([]);
      setSelectionError(null);
    }
    catch (err: any) {
      setError(err?.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    if (!open) return;
    loadData();
  }, [open, loadData]);

  const linkedIdSet = useMemo(() => {
    return new Set(linkedConsortiums.map((item) => item.consortiumId));
  }, [linkedConsortiums]);

  const selectableOptions = useMemo(() => {
    return options.filter((option) => !linkedIdSet.has(option.id));
  }, [options, linkedIdSet]);

  const addSelectedConsortiums = async () => {
    if (!portfolioId || saving) return;

    if (selectedOptions.length === 0) {
      setSelectionError('Selecteer minimaal 1 samenwerkingsverband.');
      return;
    }

    setSelectionError(null);
    setSaving(true);
    setError(null);

    try {
      const selectedToAdd = [...selectedOptions];

      await Promise.all(
        selectedToAdd.map((item) =>
          portfolioService.addConsortiumToPortfolio(portfolioId, item.id)
        )
      );

      setLinkedConsortiums((prev) => {
        const existingIds = new Set(prev.map((item) => item.consortiumId));
        const merged = [
          ...prev,
          ...selectedToAdd
            .filter((item) => !existingIds.has(item.id))
            .map((item) => ({
              consortiumId: item.id,
              name: item.name,
              members: item.members,
            })),
        ];

        return merged.sort((a, b) => a.name.localeCompare(b.name, 'nl-NL'));
      });

      setSelectedOptions([]);
      onUpdated();
    }
    catch (err: any) {
      setError(err?.message || 'Toevoegen van samenwerkingsverbanden is mislukt');
    }
    finally {
      setSaving(false);
    }
  };

  const removeConsortium = async (consortiumId: string) => {
    if (!portfolioId || saving) return;

    setSaving(true);
    setError(null);

    try {
      await portfolioService.removeConsortiumFromPortfolio(portfolioId, consortiumId);

      setLinkedConsortiums((prev) =>
        prev.filter((item) => item.consortiumId !== consortiumId)
      );

      setSelectedOptions((prev) =>
        prev.filter((item) => item.id !== consortiumId)
      );

      onUpdated();
    }
    catch (err: any) {
      setError(err?.message || 'Verwijderen van samenwerkingsverband is mislukt');
    }
    finally {
      setSaving(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title={isReadOnly ? 'Samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'}
      contentText={isReadOnly ? 'Dit is een overzicht van alle gekoppelde samenwerkingsverbanden.' : 'Koppel samenwerkingsverbanden aan dit afstemmingsdossier of verwijder bestaande koppelingen.'}
      onClose={onClose}
      width="900px"
      minWidth="700px"
    >
      {!isReadOnly && (
        <>
          <FormControl fullWidth sx={{ my: 1 }}>
            <Autocomplete
              multiple
              options={selectableOptions}
              value={selectedOptions}
              noOptionsText="Geen keuzes beschikbaar"
              onChange={(_event, value) => {
                setSelectedOptions(value);
                if (value.length > 0) {
                  setSelectionError(null);
                }
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{option.name}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${option.members.length} organisaties`}
                        sx={{ ml: 'auto' }}
                      />
                    </div>
                    <Typography variant="caption" color="text.secondary">
                      {option.members.length > 0 ? option.members.join(', ') : 'Geen organisaties gekoppeld'}
                    </Typography>
                  </div>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="filled" label={option.name} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Samenwerkingsverbanden toevoegen"
                  variant="filled"
                  placeholder="Zoek en selecteer een of meerdere samenwerkingsverbanden"
                  error={Boolean(selectionError)}
                  helperText={selectionError || undefined}
                />
              )}
            />
          </FormControl>

          <Box sx={{ display: 'flex', my: 0.5 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Icon>add</Icon>}
              onClick={addSelectedConsortiums}
              disabled={saving || selectedOptions.length === 0}
              sx={{ width: 'auto' }}
            >
              Toevoegen
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Huidige samenwerkingsverbanden ({linkedConsortiums.length})
      </Typography>

      <List dense>
        {linkedConsortiums.map((item) => (
          <ListItem
            key={item.consortiumId}
            secondaryAction={
              !isReadOnly && (
                <Tooltip title="Samenwerkingsverband verwijderen" placement="right" arrow>
                  <IconButton edge="end" onClick={() => removeConsortium(item.consortiumId)}>
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )
            }
          >
            <ListItemText
              primary={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
                  <Typography component="span">{item.name}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${item.members.length} organisaties`}
                  />
                </Box>
              )}
              secondary={
                item.members.length > 0 ? item.members.join(', ') : 'Geen organisaties gekoppeld'
              }
            />
          </ListItem>
        ))}

        {linkedConsortiums.length === 0 && (
          <ListItem>
            <ListItemText primary="Nog geen samenwerkingsverbanden" />
          </ListItem>
        )}
      </List>

      {(loading || saving) && <ProgressBar />}
      {error && <ErrorMessage message={error} />}
    </DialogBase>
  );
};

export default DialogPortfolioConsortiums;
